// Menu hub. Sub-views: home (mode select) and online (ranked queue / private
// room + wallet link); PLAY VS AI hands off to the FIGHTER SELECT scene
// (scenes/select.ts). Chicken Arcade badge links back to /arcade. All UI is
// canvas immediate-mode.

import type { App, Scene } from "../core/app";
import type { Rect } from "../core/input";
import { PALETTE, ARCADE_URL } from "../game/config";
import { hasWallet } from "../game/net";
import { session } from "../game/session";
import { drawLogo, drawButton, text } from "../art/draw";

type View = "home" | "online";

export class MenuScene implements Scene {
  private t = 0;
  private view: View = "home";
  private roomInput = "";
  private status = "";
  private walletLinked = false;

  constructor(private app: App) {
    if (!app.storage.data.name) {
      app.storage.data.name = `Rooster${100 + Math.floor(Math.random() * 900)}`;
      app.storage.save();
    }
  }

  enter(): void {
    // Invite link (?room=CODE): jump straight into that private room.
    if (session.pendingRoom) {
      const code = session.pendingRoom;
      session.pendingRoom = null;
      this.startOnline("room", code);
    }
  }

  update(dt: number): void {
    this.t += dt;
    const { app } = this;
    // Global: arcade badge + mute.
    if (app.input.clicked(this.arcadeRect())) {
      window.location.href = ARCADE_URL;
      return;
    }
    if (app.input.clicked(this.muteRect())) app.audio.toggleMute();

    if (this.view === "home") this.updateHome();
    else this.updateOnline();
  }

  // ---- rects ----
  private arcadeRect(): Rect {
    return { x: 12, y: 12, w: 150, h: 36 };
  }
  private muteRect(): Rect {
    return { x: this.app.w - 52, y: 12, w: 40, h: 40 };
  }
  private homeButtons(): { key: string; label: string; r: Rect }[] {
    const bw = Math.min(this.app.w - 60, 320);
    const x = this.app.w / 2 - bw / 2;
    const y0 = this.app.h * 0.46;
    const h = 58;
    const gap = 14;
    const defs = [
      { key: "ai", label: "PLAY VS AI" },
      { key: "online", label: "ONLINE" },
      { key: "leaderboard", label: "LEADERBOARD" },
    ];
    return defs.map((d, i) => ({ ...d, r: { x, y: y0 + i * (h + gap), w: bw, h } }));
  }
  private nameRect(): Rect {
    const bw = Math.min(this.app.w - 60, 320);
    return { x: this.app.w / 2 - bw / 2, y: this.app.h * 0.46 + 3 * 72 + 6, w: bw, h: 34 };
  }

  private backRect(): Rect {
    return { x: 12, y: this.app.h - 56, w: 110, h: 44 };
  }

  // ---- home ----
  private updateHome(): void {
    const { app } = this;
    for (const b of this.homeButtons()) {
      if (app.input.clicked(b.r)) {
        app.audio.play("click");
        if (b.key === "ai") void import("./select").then((m) => app.setScene(new m.SelectScene(app)));
        else if (b.key === "online") this.view = "online";
        else void import("./leaderboard").then((m) => app.setScene(new m.LeaderboardScene(app)));
      }
    }
    if (app.input.clicked(this.nameRect())) this.editName();
  }

  private editName(): void {
    const cur = this.app.storage.data.name;
    const next = window.prompt("Your rooster's name:", cur);
    if (next && next.trim()) {
      this.app.storage.data.name = next.trim().slice(0, 16);
      this.app.storage.save();
    }
  }

  // ---- online ----
  private onlineButtons(): { key: string; label: string; r: Rect }[] {
    const bw = Math.min(this.app.w - 60, 340);
    const x = this.app.w / 2 - bw / 2;
    const y0 = this.app.h * 0.34;
    const h = 54;
    const gap = 12;
    const defs = [
      { key: "ranked", label: "RANKED QUEUE" },
      { key: "createRoom", label: "CREATE PRIVATE ROOM" },
      { key: "joinRoom", label: "JOIN ROOM (CODE)" },
      { key: "wallet", label: this.walletLinked ? "WALLET LINKED ✓" : "CONNECT WALLET TO RANK" },
    ];
    return defs.map((d, i) => ({ ...d, r: { x, y: y0 + i * (h + gap), w: bw, h } }));
  }

  private updateOnline(): void {
    const { app } = this;
    if (app.input.clicked(this.backRect())) {
      app.audio.play("click");
      this.view = "home";
      return;
    }
    for (const b of this.onlineButtons()) {
      if (!app.input.clicked(b.r)) continue;
      app.audio.play("click");
      if (b.key === "ranked") this.startOnline("ranked", "");
      else if (b.key === "createRoom") this.startOnline("room", this.genRoom(), true);
      else if (b.key === "joinRoom") {
        const code = window.prompt("Enter 4-char room code:", this.roomInput)?.trim().toUpperCase() ?? "";
        if (code.length >= 3) this.startOnline("room", code);
      } else if (b.key === "wallet") this.linkWallet();
    }
  }

  private genRoom(): string {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let s = "";
    for (let i = 0; i < 4; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return s;
  }

  private async linkWallet(): Promise<void> {
    if (!hasWallet()) {
      this.status = "No Phantom wallet detected. You can still play unranked.";
      return;
    }
    try {
      const net = await import("../game/net");
      const { session } = await import("../game/session");
      session.walletLink = await net.linkWallet();
      this.walletLinked = true;
      this.status = "Wallet linked. Ranked results will count.";
    } catch (e) {
      this.status = (e as Error).message;
    }
  }

  private startOnline(mode: "ranked" | "room", room: string, create = false): void {
    const { app } = this;
    app.flow.mode = "online";
    app.flow.netMode = mode;
    app.flow.room = room;
    app.flow.roomCreate = create;
    void import("./online").then((m) => app.setScene(new m.OnlineScene(app)));
  }

  // ---- render ----
  render(): void {
    const { app } = this;
    const ctx = app.ctx;
    app.clear(PALETTE.bg);
    // Ambient arena-ish backdrop.
    const g = ctx.createRadialGradient(app.w / 2, app.h * 0.3, 0, app.w / 2, app.h * 0.3, app.h * 0.7);
    g.addColorStop(0, "rgba(139,92,246,0.14)");
    g.addColorStop(1, "rgba(139,92,246,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, app.w, app.h);

    // Arcade badge + mute (all views).
    const a = this.arcadeRect();
    drawButton(ctx, a.x, a.y, a.w, a.h, "‹ CHICKEN ARCADE", { size: 12, hover: app.input.hover(a), fill: "rgba(20,20,28,0.7)" });
    const mu = this.muteRect();
    drawButton(ctx, mu.x, mu.y, mu.w, mu.h, app.audio.muted ? "♪̸" : "♪", { size: 16, hover: app.input.hover(mu), fill: "rgba(20,20,28,0.7)" });

    if (this.view === "home") this.renderHome();
    else this.renderOnline();
  }

  private renderHome(): void {
    const { app } = this;
    const ctx = app.ctx;
    drawLogo(ctx, app.w / 2, app.h * 0.28, Math.min(app.w, app.h) * 0.06, app.assets.get("logo"), this.t);
    for (const b of this.homeButtons()) {
      drawButton(ctx, b.r.x, b.r.y, b.r.w, b.r.h, b.label, {
        active: b.key === "ai",
        hover: app.input.hover(b.r),
        size: 22,
      });
    }
    const nr = this.nameRect();
    text(ctx, `Playing as: ${app.storage.data.name}  (tap to rename)`, app.w / 2, nr.y + 22, {
      size: 13,
      color: PALETTE.muted,
      align: "center",
      mono: true,
    });
  }

  private renderOnline(): void {
    const { app } = this;
    const ctx = app.ctx;
    text(ctx, "Online", app.w / 2, app.h * 0.18, { size: 28, color: PALETTE.text, align: "center", weight: 900 });
    text(ctx, `Playing as: ${app.storage.data.name}`, app.w / 2, app.h * 0.24, { size: 14, color: PALETTE.muted, align: "center", mono: true });
    for (const b of this.onlineButtons()) {
      const isWallet = b.key === "wallet";
      drawButton(ctx, b.r.x, b.r.y, b.r.w, b.r.h, b.label, {
        active: b.key === "ranked",
        hover: app.input.hover(b.r),
        size: 18,
        fill: isWallet && this.walletLinked ? "#1c2a1c" : undefined,
        border: isWallet && this.walletLinked ? "#3ddc84" : undefined,
      });
    }
    if (this.status) {
      text(ctx, this.status, app.w / 2, app.h * 0.34 + 4 * 66 + 8, { size: 13, color: PALETTE.muted, align: "center" });
    }
    text(ctx, "Wallet required to rank. Anyone can play unranked.", app.w / 2, app.h - 70, {
      size: 12,
      color: PALETTE.muted,
      align: "center",
    });
    this.renderBack();
  }

  private renderBack(): void {
    const b = this.backRect();
    drawButton(this.app.ctx, b.x, b.y, b.w, b.h, "‹ Back", { size: 16, hover: this.app.input.hover(b), fill: "rgba(20,20,28,0.7)" });
  }
}
